// Clerk (Google sign-in) integration constants.
//
// Clerk is used ONLY as an OAuth front-door: the browser completes the
// Google flow against Clerk, then GoogleAuthBridge exchanges the Clerk
// session token for a normal Kitdex kd_session via /auth/clerk/exchange.
// Local email/password auth is untouched.
import { publishableKeyFromHost } from "@clerk/react/internal";

// Resolves the key from the current hostname so the same build serves
// custom domains; falls back to the env-provided key.
export const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

// Canonical Replit-managed Clerk wiring: VITE_CLERK_PROXY_URL is
// intentionally undefined in development (Clerk talks to its dev Frontend
// API directly — the proxy does not work for dev instances) and is
// auto-provisioned by the Replit publish system for production builds,
// pointing at the same-origin /api/__clerk proxy mounted in app.ts.
// Do NOT hardcode the proxy path and do NOT gate this on PROD/NODE_ENV —
// either breaks the managed provisioning contract.
export const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

export const clerkEnabled = Boolean(clerkPubKey);

/**
 * Set right before redirecting to Google; GoogleAuthBridge only runs the
 * token exchange when this flag is present, so a stale Clerk browser
 * session can never silently sign a user back in after local sign-out.
 */
export const CLERK_PENDING_KEY = "kitdex_clerk_exchange_pending";

// --- Clerk sign-out registry -------------------------------------------
// auth-provider must sign out of Clerk too, but it renders whether or not
// ClerkProvider is mounted. The bridge registers the real signOut here;
// callers get a best-effort no-throw wrapper.
let clerkSignOutFn: (() => Promise<void>) | null = null;

export function registerClerkSignOut(fn: (() => Promise<void>) | null): void {
  clerkSignOutFn = fn;
}

/** Never throws — local sign-out must succeed even if Clerk's fails. */
export async function clerkSignOutBestEffort(): Promise<void> {
  try {
    await clerkSignOutFn?.();
  } catch {
    // ignore: kd_session is already destroyed; a lingering Clerk session
    // cannot re-authenticate without the pending flag.
  }
}
